import _ from "lodash";
import { useMemo } from "react";
import normalize from "json-api-normalizer";
import { getCamel, parseRubyHash } from "../../../utils/functions";
import usePermit from "./permits/usePermit";
import usePermitType from "./permits/usePermitType";
import usePermitForm from "./permits/usePermitForm";
import usePermitCustomFieldsList from "./permits/usePermitCustomFieldsList";
import { PermitFormField } from "../../../queries/permitsForms";

interface IUsePermitsContacts {
  permitId: number | string;
}

// gets all information related to the contacts on a permit
export default function usePermitContacts({ permitId }: IUsePermitsContacts) {
  const permitQuery = usePermit({ permitId: permitId });
  const permitsTypeId = useMemo(() => {
    return permitQuery.data?.data.attributes["permits-type-id"] ?? 0;
  }, [permitQuery.data]);

  // get contacts table
  const contacts = useMemo(() => {
    const contactList = getCamel(permitQuery.data, "data.attributes.permits-contacts", []); // get contact list from permit, and convert to camelcase
    return contactList.map((contact: any) => {
      // convert permitsExtensionDefId to number
      if (contact?.permitsExtensionDefId && contact.permitsExtensionDefId != null)
        contact.permitsExtensionDefId = Number(contact.permitsExtensionDefId);
      return contact;
    });
  }, [permitQuery.data]);

  const permitsExtensions = useMemo(() => {
    return permitQuery.data?.data.attributes["permits-extensions"] ?? [];
  }, [permitQuery.data]);
  const permitsExtensionIds = permitsExtensions.map((extension) => {
    return extension.data.attributes["permits-extension-def-id"];
  });

  const permitsCustomFieldsQuery = usePermitCustomFieldsList();
  const permitsCustomFields = useMemo(
    () => normalize(permitsCustomFieldsQuery.data || {})?.customField,
    [permitsCustomFieldsQuery.data],
  );

  // gets the custom fields associated with the permit's contacts
  const contactCustomFields = useMemo(
    () =>
      permitsExtensions
        .map((extension: any) => {
          const id = _.get(extension, "data.attributes.permits-extension-def-id");
          const customFieldInfo = permitsCustomFields?.[id]?.attributes;
          // if this field is a contact type, add the field's info to the array
          if (customFieldInfo != null && (customFieldInfo.type === 10 || customFieldInfo.type === 11)) {
            customFieldInfo.id = id;
            customFieldInfo.value = _.get(extension, "data.attributes.value");
            return customFieldInfo;
          }
          return null;
        })
        .filter((field: any) => field != null), // filter out non contact fields
    [permitsExtensionIds],
  );

  const permitsTypeQuery = usePermitType({ permitTypeId: permitsTypeId });

  const permitsFormId = useMemo(() => {
    return permitsTypeQuery.data?.data.attributes["permits-form-id"] ?? 0;
  }, [permitsTypeQuery.data]);

  const permitsFormQuery = usePermitForm(permitsFormId);

  const permitsFormFieldsV2 =
    permitsFormQuery.data?.included.filter((include): include is PermitFormField => {
      return include.type === "permits-form-field";
    }) ?? [];

  // gets the form fields associated with the permit's contacts
  const permitsFormFields = useMemo(() => {
    const formFields = normalize(permitsFormQuery.data || {})?.permitsFormField;

    // parse contactInfoOptions string into a JavaScript object
    if (formFields != null)
      for (const key of Object.keys(formFields)) {
        formFields[key].attributes.contactInfoOptions = parseRubyHash(formFields[key].attributes.contactInfoOptions);
      }
    return formFields;
  }, [permitsFormQuery.data]);

  return {
    contacts,
    contactCustomFields,
    permitsFormFields,
    permitsFormFieldsV2,
  };
}
